<template>
  <sfs-form-modal
    :id="modalId"
    title="SMS Alert Notifications"
    size="md"
    :static="!lazyLoad"
  >
    <b-form @submit.stop.prevent="handleSubmit">
      <b-row class="mb-2">
        <b-col class="text-center">
          <b-img
            v-if="logo"
            :src="logo"
            height="80"
            :alt="organizationName"
          />
        </b-col>
      </b-row>

      <b-row class="mb-2">
        <b-col class="text-center">
          <div class="font-w700">
            {{ organizationName }}
          </div>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <b-alert
            :show="hasBaseServerErrors()"
            variant="danger"
            dismissible
          >
            <p
              class="mb-0"
              v-for="(error, index) in baseServerErrors"
              :key="index"
            >
              {{ error }}
            </p>
          </b-alert>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <b-form-group
            label="Phone"
            label-for="sms-opt-in-phone"
          >
            <b-form-input
              id="sms-opt-in-phone"
              v-model="$v.form.phone.$model"
              type="text"
              placeholder="(201) 555-5555"
              :state="$v.form.phone.$dirty ? !$v.form.phone.$error : null"
            />
            <b-form-invalid-feedback
              v-if="$v.form.phone.$dirty"
              id="sms-opt-in-phone-feedback"
            >
              <span v-if="!$v.form.phone.required">
                Please enter a phone number to enable SMS alerts.
              </span>
              <span v-if="!$v.form.phone.maxLength">
                The phone must be less than {{ $v.form.phone.$params.maxLength.max + 1 }} characters.
              </span>
              <span v-if="!$v.form.phone.serverFailed">{{ serverErrors.phone }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <b-form-group label-for="sms-opt-in">
            <b-form-checkbox
              switch
              id="sms-opt-in"
              v-model="$v.form.sms_notifications_enabled.$model"
            >
              Enable SMS alerts for my account
            </b-form-checkbox>

            <b-form-invalid-feedback
              v-if="$v.form.sms_notifications_enabled.$dirty"
              class="d-block"
            >
              <span v-if="!$v.form.sms_notifications_enabled.validOptIn">
                A phone number is required to enable SMS alerts.
              </span>
              <span v-else-if="!$v.form.sms_notifications_enabled.serverFailed">{{ serverErrors.sms_notifications_enabled }}</span>
            </b-form-invalid-feedback>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row class="mb-2">
        <b-col>
          Opt in to receive SMS alerts from {{ programName }}. Message frequency varies. Text STOP to opt-out. Message and data rates may apply.
        </b-col>
      </b-row>

      <b-row class="mb-2 text-secondary">
        <b-col>
          For help email us at <a href="mailto:appsupport@scopesfs.com">appsupport@scopesfs.com</a> or visit our
          <a
            href="https://scopesfs.com/contact-us"
            target="_blank"
          >
            contact us page.
          </a>
        </b-col>
      </b-row>

      <b-row class="mb-2 text-secondary">
        <b-col>
          Text STOP to opt-out. Message and data rates may apply.
        </b-col>
      </b-row>

      <hr class="mt-4">

      <b-row>
        <b-col
          offset-lg="6"
          lg="3"
          order="2"
          order-lg="1"
        >
          <b-button
            variant="link-dark"
            block
            @click="closeModal"
          >
            Cancel
          </b-button>
        </b-col>
        <b-col
          lg="3"
          order="1"
          order-lg="2"
        >
          <b-button
            block
            type="submit"
            variant="primary"
            :disabled="loading"
          >
            I Agree
          </b-button>
        </b-col>
      </b-row>
    </b-form>
  </sfs-form-modal>
</template>

<script>
import { requiredIf, maxLength } from 'vuelidate/lib/validators';
import formMixin from '@/mixins/formMixin';
import formModalMixin from '@/mixins/formModalMixin';
import logo from '@/assets/images/logo.png';

export default {
  name: 'SmsOptInModal',
  mixins: [formMixin, formModalMixin],
  props: {
    lazyLoad: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      loading: true,
      user: null,
      modalId: 'sms-opt-in-modal',
      logo,
      form: {
        phone: '',
        sms_notifications_enabled: false,
      },
      fieldLabels: {
        sms_notifications_enabled: 'SMS Opt-In',
        phone: 'Phone',
      },
    };
  },
  validations: {
    form: {
      phone: {
        required: requiredIf(function () {
          return this.form.sms_notifications_enabled;
        }),
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('phone');
        },
      },
      sms_notifications_enabled: {
        required: requiredIf(function () {
          return this.isEnablingSmsOptIn;
        }),
        validOptIn() {
          return !this.isEnablingSmsOptIn || this.hasPhoneForSms;
        },
        serverFailed() {
          return !this.hasServerErrors('sms_notifications_enabled');
        },
      },
    },
  },
  computed: {
    programName() {
      return this.$store.getters.appName;
    },
    organizationName() {
      return this.$store.getters.appName;
    },
    hasPhoneForSms() {
      return !!(this.form && this.form.phone && String(this.form.phone).trim().length > 0);
    },
    isEnablingSmsOptIn() {
      return this.user && !this.user.sms_notifications_enabled && this.form.sms_notifications_enabled;
    },
  },
  mounted() {
    this.$root.$on('bv::modal::show', (_, modalId) => {
      if (modalId == this.modalId) {
        this.loading = true;
        this.loadProfile();
      }
    });
  },
  methods: {
    loadProfile() {
      this.$authAPI.getProfile().then((response) => {
        this.user = response.data;
        this.form.phone = response.data.phone || '';
        this.form.sms_notifications_enabled = !!response.data.sms_notifications_enabled;
        this.resetServerErrors();
        this.loading = false;
      });
    },
    handleSubmit() {
      if (this.isEnablingSmsOptIn && !this.hasPhoneForSms) {
        this.resetServerErrors();
        this.baseServerErrors = ['A phone number is required to enable SMS alerts for your account.'];
        return;
      }
      if (this.hasFormErrors()) {
        return;
      }
      this.loading = true;
      this.$store
        .dispatch('updateProfile', this.form)
        .then((response) => {
          this.user = response.data;
          this.form.sms_notifications_enabled = !!response.data.sms_notifications_enabled;
          this.resetServerErrors();
          this.$v.$reset();
          this.closeModal();
        })
        .catch((error) => {
          this.processServerErrors(error.response && error.response.data && error.response.data.errors);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

